import React from "react";
import { Link } from "react-router-dom";
import Section from "../layout/Section";
import Statement from "../ui/Statement";
import MicroLabel from "../ui/MicroLabel";
import TicketCard from "../ui/TicketCard";
import Reveal from "../anim/Reveal";
import Icon from "../icons/Icon";
import { SITE_CONTENT } from "../../constants/siteContent";
import styles from "./Testimonials.module.css";

/* Testimonials — "HOW WE WORK": an HONEST set of delivery principles, laid out
 * as the reference's fanned-cards block. It is not a quote carousel.
 *
 * Appinfigo has no client quotes cleared for publication, so this section does
 * NOT pretend to have any. There are deliberately no avatars, no initials, no
 * names attached to statements, and no video reel: everything here is a
 * commitment Appinfigo makes in its own voice, taken verbatim from
 * SITE_CONTENT.testimonials — whose own subtitle states the position outright
 * ("We will not put fake testimonials on this page").
 *
 * TODO(content): when a real client agrees to go on record, add their quote
 * with a full name, role and company to SITE_CONTENT and give named quotes
 * their own section. Do not re-attach names to the principles below — they are
 * promises from the company, not customer feedback.
 *
 * Layout: from 1280px the cards are a FAN — one centred row, rotated 4deg per
 * step away from the middle (-8 / -4 / 0 / 4 / 8 for five), overlapping, the
 * middle card on top and largest. Each neighbour covers only the padding of the
 * card beneath it, so every word stays readable without hovering. 981–1279px is
 * a lightly tilted 3-up grid; <= 980px is a plain 2-up grid, then one column.
 * The geometry lives in Testimonials.module.css and is driven by the three
 * numbers set on each <li> below.
 */

const CONTENT = SITE_CONTENT.testimonials;

// TODO(content): UI labels for this block. The headline itself is NOT here — it
// is SITE_CONTENT.testimonials.title, re-lined: the last `accentWords` words
// set in the serif on a second line, the rest in the condensed sans above it.
const SECTION = {
  id: "how-we-work",
  titleId: "how-we-work-title",
  eyebrow: "How we work",
  emblem: "quality", // an <Icon> glyph name — see src/components/icons/Icon.js
  accentWords: 3, // the serif voice carries one to three words, never more
  actionLabel: "How we engage",
  actionTo: "/engagement-model"
};

// TODO(content): the fan shows the FIRST FIVE commitments, in the order they are
// stored in SITE_CONTENT.testimonials.items — reorder them there to feature a
// different set. Five is also the ceiling: the fan's widths are drawn for five
// cards across, and an odd count keeps one card dead centre.
const MAX_CARDS = 5;

const PRINCIPLES = (CONTENT.items || []).slice(0, MAX_CARDS);

const TITLE_WORDS = String(CONTENT.title || "")
  .trim()
  .split(/\s+/)
  .filter(Boolean);
const ACCENT_COUNT = Math.max(
  0,
  Math.min(SECTION.accentWords, 3, TITLE_WORDS.length - 1)
);
const TITLE_SANS = TITLE_WORDS.slice(0, TITLE_WORDS.length - ACCENT_COUNT).join(" ");
const TITLE_SERIF = TITLE_WORDS.slice(TITLE_WORDS.length - ACCENT_COUNT).join(" ");

export default function Testimonials() {
  const middle = (PRINCIPLES.length - 1) / 2;

  return (
    <Section
      tone="paper"
      size="lg"
      container="wide"
      id={SECTION.id}
      aria-labelledby={SECTION.titleId}
    >
      <div className={styles.head}>
        <Reveal className={styles.mark}>
          <Icon name={SECTION.emblem} size={40} />
          <MicroLabel as="p" className={styles.eyebrow}>
            {SECTION.eyebrow}
          </MicroLabel>
        </Reveal>

        {/* An h2: HeadingWipe (App.js) owns its motion — never wrap it in Reveal. */}
        <Statement
          as="h2"
          size="h2"
          align="center"
          id={SECTION.titleId}
          className={styles.title}
        >
          {TITLE_SANS}
          {TITLE_SERIF ? (
            <>
              {" "}
              <br />
              <em>{TITLE_SERIF}</em>
            </>
          ) : null}
        </Statement>

        <Reveal as="p" className={styles.lead} delay={80}>
          {CONTENT.subtitle}
        </Reveal>
      </div>

      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- list-style: none strips list semantics in Safari/VoiceOver; the explicit role restores them */}
      <ul className={styles.fan} role="list">
        {PRINCIPLES.map((item, index) => {
          const offset = index - middle;
          const distance = Math.abs(offset);
          const side = offset < 0 ? "left" : offset > 0 ? "right" : "center";

          return (
            <li
              key={item.name}
              className={styles.slot}
              data-side={side}
              style={{
                "--fan-offset": offset,
                "--fan-distance": distance,
                "--fan-z": Math.round((PRINCIPLES.length - distance) * 2)
              }}
            >
              {/* Reveal is a WRAPPER here, not the card: the card carries its own
                  transform (the fan rotation), which Reveal's would replace. */}
              <Reveal className={styles.rise} delay={Math.round(distance * 90)}>
                <TicketCard tone="olive" className={styles.card}>
                  <div className={styles.cardTop} aria-hidden="true">
                    <MicroLabel className={styles.index}>
                      {String(index + 1).padStart(2, "0")}
                    </MicroLabel>
                    <Icon name="check" size={16} className={styles.tick} />
                  </div>

                  <h3 className={styles.name}>{item.name}</h3>
                  <MicroLabel as="p" className={`micro--accent ${styles.caption}`}>
                    {item.role}
                  </MicroLabel>

                  <p className={styles.body}>{item.text}</p>
                </TicketCard>
              </Reveal>
            </li>
          );
        })}
      </ul>

      <Reveal className={styles.action}>
        <Link className="link-arrow" to={SECTION.actionTo}>
          {SECTION.actionLabel}
        </Link>
      </Reveal>
    </Section>
  );
}
